import React, { useState, useEffect, useCallback } from 'react';
import { maxAPI } from '../services/api';
import { MessageCircle, Plus, RefreshCw, Power, KeyRound, Copy, Users } from 'lucide-react';
import toast from 'react-hot-toast';

// Карточка-объяснение: webhook действующего бота НЕ переключается.
const WEBHOOK_NOTE =
  'Segmira НЕ переключает webhook вашего бота. Действующие уведомления о заказах ' +
  'продолжают работать. Ваш backend лишь дублирует события в Segmira (fan-out).';

function SecretOnceModal({ secret, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h2 className="modal-title"><KeyRound size={18} /> Ingest secret (показывается один раз)</h2>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
          Сохраните секрет — он нужен адаптеру бота для подписи fan-out запросов.
          Повторно показать нельзя; при утере используйте ротацию.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="form-input" readOnly value={secret} style={{ fontFamily: 'monospace' }} />
          <button className="btn" onClick={() => { navigator.clipboard?.writeText(secret); toast.success('Скопировано'); }}>
            <Copy size={16} />
          </button>
        </div>
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button className="btn btn-primary" onClick={onClose}>Я сохранил секрет</button>
        </div>
      </div>
    </div>
  );
}

function ConnectWizard({ onClose, onCreated }) {
  const [form, setForm] = useState({ mode: 'existing', title: 'MAX', token: '' });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!form.token.trim()) { toast.error('Введите токен бота'); return; }
    setLoading(true);
    try {
      const { data } = await maxAPI.createConnection(form);
      toast.success('Подключение создано');
      onCreated(data);           // data.ingest_secret показываем один раз
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка создания'); }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h2 className="modal-title"><Plus size={18} /> Подключить MAX-бота (Existing Bot)</h2>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>{WEBHOOK_NOTE}</p>
        <div className="form-group">
          <label className="form-label">Название подключения</label>
          <input className="form-input" value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-label">Токен бота (из @MasterBot)</label>
          <input className="form-input" type="password" autoComplete="off" value={form.token}
            onChange={e => setForm({ ...form, token: e.target.value })}
            placeholder="вставьте токен — он будет зашифрован и замаскирован" />
        </div>
        <div style={{ textAlign: 'right' }}>
          <button className="btn" onClick={onClose} style={{ marginRight: 8 }}>Отмена</button>
          <button className="btn btn-primary" disabled={loading} onClick={submit}>
            {loading ? 'Создание…' : 'Создать'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = { active: 'badge-success', connecting: 'badge-warning', disconnected: 'badge-muted', error: 'badge-danger', imported: 'badge-muted', stopped: 'badge-muted', blocked: 'badge-danger' };
  return <span className={`badge ${map[status] || 'badge-muted'}`}>{status}</span>;
}

// Статус CRM-связи. verified показываем ТОЛЬКО при реальной привязке (segmira_client_id);
// candidate/conflict — явно не verified (неподтверждённые).
function CrmBadge({ status, linked }) {
  const eff = linked ? 'verified' : (status || 'unlinked');
  const map = {
    verified: ['badge-success', 'подтверждён'],
    candidate: ['badge-warning', 'кандидат'],
    conflict: ['badge-danger', 'конфликт'],
    unlinked: ['badge-muted', '—'],
  };
  const [cls, label] = map[eff] || map.unlinked;
  return <span className={`badge ${cls}`}>{label}</span>;
}

function ConnectionDetail({ conn, onChanged }) {
  const [subs, setSubs] = useState(null);      // null = ещё не загружено
  const [stats, setStats] = useState(null);
  const [secret, setSecret] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [q, setQ] = useState('');   // поиск по MAX user ID

  const load = useCallback(async () => {
    setLoadErr(false);
    try {
      const [s, st] = await Promise.all([maxAPI.subscribers(conn.id), maxAPI.stats(conn.id)]);
      setSubs(Array.isArray(s.data) ? s.data : (s.data?.items || []));
      setStats(st.data);
    } catch (e) {
      // не глотаем: показываем ошибку блока + повтор; прежние данные НЕ затираем
      setLoadErr(true);
    }
  }, [conn.id]);
  useEffect(() => { load(); }, [load]);

  const verify = async () => { try { await maxAPI.verify(conn.id); toast.success('Проверено'); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка'); } };
  const disconnect = async () => { if (!window.confirm('Отключить подключение? Webhook бота не затрагивается.')) return; try { await maxAPI.disconnect(conn.id); toast.success('Отключено'); onChanged(); } catch (e) { toast.error('Ошибка'); } };
  const rotate = async () => { if (!window.confirm('Сгенерировать новый ingest secret? Старый перестанет работать.')) return; try { const { data } = await maxAPI.rotateIngestSecret(conn.id); setSecret(data.ingest_secret); } catch (e) { toast.error('Ошибка'); } };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>{conn.title}</h3>
        <StatusBadge status={conn.status} />
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          бот: {conn.bot_username || conn.bot_user_id || '—'} · токен: {conn.token_masked}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn" onClick={verify}><RefreshCw size={16} /> Проверить</button>
          <button className="btn" onClick={rotate}><KeyRound size={16} /> Ротация секрета</button>
          <button className="btn btn-danger" onClick={disconnect}><Power size={16} /> Отключить</button>
        </div>
      </div>

      {conn.last_error && <p style={{ color: 'var(--danger)', fontSize: 13 }}>Ошибка: {conn.last_error}</p>}

      {loadErr && (
        <div className="card" style={{ borderColor: 'var(--danger)', marginTop: 12 }}>
          <span style={{ color: 'var(--danger)', fontSize: 13 }}>
            Не удалось загрузить подписчиков и статистику (ошибка API или нет доступа).
          </span>
          <button className="btn btn-sm" style={{ marginLeft: 12 }} onClick={load}>Повторить</button>
        </div>
      )}

      {stats && (
        <div className="stat-grid" style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12 }}>
          <div className="stat-card"><div className="stat-card-value">{stats.subscribers_total}</div><div className="stat-card-label">Всего подписчиков</div></div>
          <div className="stat-card"><div className="stat-card-value">{stats.subscribers_imported ?? 0}</div><div className="stat-card-label">Импортированы (статус неизвестен)</div></div>
          <div className="stat-card"><div className="stat-card-value">{stats.subscribers_active}</div><div className="stat-card-label">Активных</div></div>
          <div className="stat-card"><div className="stat-card-value">{stats.subscribers_stopped}</div><div className="stat-card-label">Остановили бота</div></div>
          <div className="stat-card"><div className="stat-card-value">{stats.new_today ?? 0}</div><div className="stat-card-label">Новых сегодня</div></div>
          <div className="stat-card"><div className="stat-card-value">{stats.new_7d ?? 0}</div><div className="stat-card-label">Новых за 7 дней</div></div>
          <div className="stat-card"><div className="stat-card-value">{stats.linked_clients}</div><div className="stat-card-label">Связано с CRM (verified)</div></div>
          <div className="stat-card"><div className="stat-card-value">{stats.phone_verified}</div><div className="stat-card-label">Телефон подтверждён</div></div>
        </div>
      )}

      {stats && (
        <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-muted)' }}>
          Источники: {Object.entries(stats.by_source || {}).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'}<br />
          Последнее событие: {stats.last_event_at || '—'} · heartbeat: {stats.last_heartbeat_at || '—'}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
        <h4 style={{ margin: 0 }}><Users size={16} /> Подписчики</h4>
        <input className="form-input" style={{ maxWidth: 260 }} placeholder="Поиск по MAX user ID"
          value={q} onChange={e => setQ(e.target.value.trim())} />
      </div>
      <table className="table">
        <thead><tr><th>MAX user</th><th>Статус</th><th>Источник</th><th>Телефон</th><th>CRM-связь</th><th>Первый контакт</th><th>Последняя активность</th></tr></thead>
        <tbody>
          {(subs || []).filter(s => !q || String(s.max_user_id).includes(q)).map(s => (
            <tr key={s.id}>
              <td style={{ fontFamily: 'monospace' }}>{s.max_user_id}</td>
              <td><StatusBadge status={s.status} /></td>
              <td>{s.source || '—'}</td>
              <td>{s.phone_verified ? '✓ подтверждён' : '—'}</td>
              <td><CrmBadge status={s.crm_link_status} linked={!!s.segmira_client_id} /></td>
              <td>{s.first_seen_at || '—'}</td>
              <td>{s.last_seen_at || '—'}</td>
            </tr>
          ))}
          {subs === null && !loadErr && <tr><td colSpan={7} style={{ color: 'var(--text-muted)' }}>Загрузка…</td></tr>}
          {loadErr && <tr><td colSpan={7} style={{ color: 'var(--danger)' }}>Ошибка загрузки списка — см. выше «Повторить»</td></tr>}
          {subs !== null && !loadErr && subs.length === 0 && <tr><td colSpan={7} style={{ color: 'var(--text-muted)' }}>Пока нет подписчиков</td></tr>}
          {subs !== null && !loadErr && subs.length > 0 && subs.filter(s => !q || String(s.max_user_id).includes(q)).length === 0 &&
            <tr><td colSpan={7} style={{ color: 'var(--text-muted)' }}>Ничего не найдено по «{q}»</td></tr>}
        </tbody>
      </table>

      {secret && <SecretOnceModal secret={secret} onClose={() => setSecret(null)} />}
    </div>
  );
}

export default function MaxConnectorPage() {
  const [conns, setConns] = useState([]);
  const [wizard, setWizard] = useState(false);
  const [newSecret, setNewSecret] = useState(null);
  // loading | ready | off (404, фича выключена) | error (403/5xx/сеть)
  const [state, setState] = useState('loading');

  const load = useCallback(async () => {
    setState('loading');
    try {
      const { data } = await maxAPI.listConnections();
      setConns(Array.isArray(data) ? data : (data?.items || []));
      setState('ready');
    } catch (e) {
      const s = e.response?.status;
      // 404 -> выключено; 401 -> глобальный интерсептор (redirect на /login);
      // 403/5xx/сеть -> ошибка (НЕ пустой список). conns НЕ очищаем при сбое refresh.
      setState(s === 404 ? 'off' : 'error');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="page">
      <div className="page-header">
        <h1><MessageCircle size={22} /> MAX Мессенджер</h1>
        {state === 'ready' && <button className="btn btn-primary" onClick={() => setWizard(true)}><Plus size={16} /> Подключить бота</button>}
      </div>

      {state === 'loading' && <div className="card" style={{ color: 'var(--text-muted)' }}>Загрузка…</div>}

      {state === 'off' && (
        <div className="card">
          Модуль MAX Connector выключен на сервере (feature flag). Включение — на стороне администратора Segmira.
        </div>
      )}

      {state === 'error' && (
        <div className="card" style={{ borderColor: 'var(--danger)' }}>
          <span style={{ color: 'var(--danger)' }}>Не удалось загрузить подключения (ошибка сервера или нет доступа).</span>
          <button className="btn btn-sm" style={{ marginLeft: 12 }} onClick={load}>Повторить</button>
        </div>
      )}

      {state === 'ready' && conns.length === 0 && (
        <div className="card">
          <p>Нет подключений. «Подключить бота» → режим Existing Bot.</p>
          <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{WEBHOOK_NOTE}</p>
        </div>
      )}

      {/* показываем подключения в ready; при error-во-время-refresh не прячем уже загруженные */}
      {(state === 'ready' || (state === 'error' && conns.length > 0)) &&
        conns.map(c => <ConnectionDetail key={c.id} conn={c} onChanged={load} />)}

      {wizard && <ConnectWizard onClose={() => setWizard(false)}
        onCreated={(data) => { setNewSecret(data.ingest_secret); load(); }} />}
      {newSecret && <SecretOnceModal secret={newSecret} onClose={() => setNewSecret(null)} />}
    </div>
  );
}
